import {
  BaseComposedElement,
  ComposedConditionSet,
  ComposedSurveyPlugin,
  Element,
  QuestionType,
  ResponseRecord,
  SessionStatusBot,
  SubmissionInfo,
  SubmissionTypes,
  SurveyVariableDefinition,
  SurveyVariables,
} from '@hyper-inferno/survey-elements-core';

import { findKey } from 'lodash';

export type ComposedElement = BaseComposedElement;
// | OpenEndedComposedElement
// | SingleChoiceComposedElement
// | NumericComposedElement
// | ImageComposedElement
// | VideoComposedElement
// | VoiceComposedElement
// | DropDownSingleChoiceComposedElement
// | MultiDropDownComposedElement
// | RankingComposedElement;

export class ComposedElementBuilder {
  addConditionalDisplayMeta(conditionalDisplayManifest: ComposedConditionSet) {
    this.composedElement.conditional_display_manifest =
      conditionalDisplayManifest;
  }
  private composedElement: ComposedElement;
  private _element: Element;

  constructor(element: Element) {
    this._element = element;
    this.composedElement = {
      questionText: element.question_text,
      questionMeta: {},
      type: element.type as QuestionType,
      name: element.name,
      id: element.id,
      submissionInfo: {
        submissionType: SubmissionTypes.CLIENT_RESPONSE,
        submissionPayload: {
          sys_disposition_code: '',
        },
        statusChange: SessionStatusBot.IN_PROGRESS,
      },
      element_type: element.element_type, // or whatever mapping is appropriate
      page_id: element.page_id, // Initialized as empty or from element
      rank: element.rank, // Or a default starting rank
      answerObject: {}, // This was commented out, but the error says it's required
      required: element.required ?? true,
      elementPlugins: [],
      rowAnswerObject: {},

      //   attachment: {
      //     type: QuestionAttachmentType.NONE,
      //   },
      //   questionImage: question.questionImage,
      //   questionErrorMessage: '',
      //   conditionalPreview: question.customProps?.['conditionalViewQuery'],
      //   filterType: question.customProps?.['filterType'],
      //   filterOptions: question.customProps?.['filterOptions'],
      //   filterQuestion: question.customProps?.['filterQuestion'],
      //   isRandomize: question.customProps?.['isRandomize'],

      // ✅ Added line to satisfy interface
      //   options: question.options ?? [],
    };
  }

  setQuestionText(message: string): this {
    this.composedElement.questionText = message;
    return this;
  }

  //   setQuestionErrorMessage(message: string): this {
  //     this.composedQuestion.questionErrorMessage = message;
  //     return this;
  //   }

  setQuestionVariables(variables: SurveyVariables): this {
    this.composedElement.questionMeta = variables;
    return this;
  }

  setAnswerObject(responses: ResponseRecord): this {
    this.composedElement.answerObject = responses;
    return this;
  }

  setRowAnswerObject(row_option_key: number, responses: ResponseRecord): this {
    this.composedElement.rowAnswerObject[row_option_key] = responses;
    return this;
  }

  setSubmissionInfo(payload: SubmissionInfo): this {
    if (payload) {
      this.composedElement.submissionInfo = payload;
    }
    return this;
  }

  getVariables() {
    return this.composedElement.questionMeta;
  }

  getVariableByCriteria(cond: Partial<SurveyVariableDefinition>) {
    const firstMatchingKey = this.getVariableKeyByCriteria(cond);

    if (firstMatchingKey) return this.getVariables()[firstMatchingKey];
    else return undefined;
  }

  getVariableKeyByCriteria(
    cond: Partial<SurveyVariableDefinition>,
  ): string | undefined {
    const firstMatchingKey = findKey(this.getVariables(), cond);

    if (firstMatchingKey) return firstMatchingKey;
    else return undefined;
  }

  getConfig() {
    return (this.composedElement as any)[this.composedElement.type];
  }

  getSubmissionInfo() {
    return this.composedElement.submissionInfo;
  }

  //   setPageNavigation(
  //     jsn_activity_order: { questionId: string; firstVisit: string }[],
  //   ) {
  //     // Extract relevant data

  //     // Parse the activity order into an array of question IDs
  //     const activityOrder = jsn_activity_order
  //       ? jsn_activity_order.map((q: any) => q.questionId)
  //       : [];

  //     // Determine the index of the current question
  //     const currentIndex = activityOrder.indexOf(this._question.id);
  //     const lastIndex = activityOrder.length - 1;

  //     // Logic to determine button visibility
  //     let showBackwardButton = currentIndex > 0; // Backward button if not the first element
  //     let showForwardButton = currentIndex < lastIndex; // Forward button if not the last element

  //     // Handle edge cases for empty or single-element arrays
  //     if (activityOrder.length <= 1) {
  //       showForwardButton = false;
  //       showBackwardButton = false;
  //     }
  //     this.composedQuestion.navigation = {
  //       showForwardButton,
  //       showBackwardButton,
  //       isSkippable: this._question.isSkippable,
  //       captureLocation: this._question.captureLocation ?? false,
  //     };

  //     return this;
  //   }

  setQuestionConfig(questionConfig: any) {
    const type = this.composedElement.type as QuestionType;
    (this.composedElement as any)[type] = questionConfig;
    return this;
  }

  setElementPlugins(plugins: ComposedSurveyPlugin[]): this {
    const safePlugins = plugins || [];
    this.composedElement.elementPlugins = safePlugins;

    const type = this.composedElement.type;
    if (!(this.composedElement as any)[type]) {
      (this.composedElement as any)[type] = {};
    }

    safePlugins.forEach((plugin) => {
      const overrides = plugin?.variable_schema?.confOverides;
      const values = plugin?.instanceValues;

      if (Array.isArray(overrides) && values) {
        // 1. Filter out keys that don't exist in values, then map to [key, value] pairs
        const entries = overrides
          .filter((key) => key in values)
          .map((key) => [key, values[key]]);

        // 2. Merge everything into the target object at once, eliminating the inner loop

        // 1. Clone the nested config block cleanly
        const targetConfig = { ...(this.composedElement as any)[type] };

        // 2. Apply updates to the clone
        Object.assign(targetConfig, Object.fromEntries(entries));

        // 3. Reassign back to top-level object to trigger upstream setters
        (this.composedElement as any)[type] = targetConfig;
      }
    });

    return this;
  }

  //   setTheme(themeConfig: {
  //     globalHeader: string;
  //     questionHeader: Question['customUi'];
  //     questionFooter: Question['footer'];
  //     globalFooter: string;
  //   }) {
  //     this.composedQuestion.pageLayout = {
  //       globalHeader: themeConfig.globalHeader,
  //       questionHeader: themeConfig.questionHeader,
  //       questionFooter: themeConfig.questionFooter,
  //       globalFooter: themeConfig.globalFooter,
  //     };
  //     return this;
  //   }

  //   setAttachment() {
  //     const mediaFetchUrl =
  //       'https://demo.infolinetech.in/api/media/fetch/' +
  //       this._question.questionImageId;

  //     this._question.questionImageId;
  //     this.composedQuestion.attachment = {
  //       type: this._question.questionAttachmentType,
  //       mediaUrl: mediaFetchUrl,
  //     };

  //     return this;
  //   }

  build(): ComposedElement {
    return this.composedElement;
  }
}
